import { ActionBar, ActionBarButton } from 'components/common/ActionBar'
import { CheckOutlined, ChevronLeftOutlined, ChevronRightOutlined, DeleteOutlineOutlined, UndoOutlined } from '@mui/icons-material'
import { FC, ReactElement, forwardRef } from 'react'
import { NARROW_PHONE_QUERY, useMatchMedia } from './useMatchMedia'

import { CampaignPostDTO } from 'models/socialCampaign'
import { GRAY_700 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { ReviewState } from 'utils/socialCampaign/postEditor'
import { TFunction } from 'i18next'
import Typography from '@mui/material/Typography'
import styles from './CampaignPostEditor.module.sass'
import { useTranslation } from 'react-i18next'

/** What the editor offers on the post shown, decided by the editor and drawn in its foot and in the phone's bar. */
export interface PostEditorActionsModel {
  /** Left of the actions: the drafts left to review, or why the main action waits */
  note: string | null
  /** The note says why the main action waits, and describes it */
  isNoteAReason: boolean
  /** Remove's label; absent when the post cannot be removed */
  removeLabel?: string
  /** Undo approval shows (an approved draft) */
  canUndo: boolean
  /** The main action: its foot and phone labels, its glyph, whether a change that cannot be saved holds it */
  primary: { label: string, phoneLabel: string, icon?: ReactElement, isHeldBack: boolean, onClick: () => void }
  /** The approved draft's bar: Remove shrinks to its bin so Undo approval and Next fit */
  isRemoveIconOnlyOnPhone: boolean
  /** The review's last draft: on a narrow phone its bar drops the glyphs so both labels fit */
  isLaunch: boolean
}

interface ActionsContext {
  /** The post shown; null while one is written by hand */
  post: CampaignPostDTO | null
  /** Where the review stands, for a draft */
  review: ReviewState | null
  /** A post comes after this one: an approved draft walks on to it */
  hasNext: boolean
  /** The campaign is a draft: its last draft launches it (else schedules the new drafts) */
  draftCampaign: boolean
  /** The post can be removed (edit mode) */
  removable: boolean
  /** The form holds a change */
  dirty: boolean
  /** Why the change cannot be saved, if it cannot */
  blockerText: string | null
  t: TFunction
  on: { approve: () => void, launch: () => void, next: () => void, close: () => void, create: () => void }
}

/**
 * The actions of each mode (prototype v3 `edFoot`). Review: Remove (and launch, on the last draft), Undo approval once
 * approved, then Approve & next, Next, Approve & launch or Launch. Edit: Remove when it can go, Save changes or Done.
 * Create: Schedule the post. A change that cannot be saved holds the main action back and says why.
 */
export const postEditorActionsOf = ({ post, review, hasNext, draftCampaign, removable, dirty, blockerText, t, on }: ActionsContext): PostEditorActionsModel => {
  const isHeldBack = dirty && !!blockerText

  if (post && review) {
    let primary: Omit<PostEditorActionsModel['primary'], 'isHeldBack'>
    if (review.last && post.reviewed) {
      const label = t(draftCampaign ? 'bar.launch' : 'editor.schedule_all')
      primary = { label, phoneLabel: label, icon: <CheckOutlined />, onClick: on.launch }
    } else if (review.last) {
      primary = {
        label: t(draftCampaign ? 'editor.approve_launch' : 'editor.approve_schedule'),
        phoneLabel: t(draftCampaign ? 'editor.approve_launch_short' : 'editor.approve_schedule'),
        icon: <CheckOutlined />,
        onClick: on.approve,
      }
    } else if (post.reviewed) {
      const label = hasNext ? t('editor.next') : t('editor.done')
      primary = { label, phoneLabel: label, onClick: hasNext ? on.next : on.close }
    } else {
      const label = t('editor.approve_next')
      primary = { label, phoneLabel: label, icon: <CheckOutlined />, onClick: on.approve }
    }
    return {
      note: isHeldBack ? blockerText : review.left ? t('editor.left', { count: review.left }) : t(draftCampaign ? 'editor.last' : 'editor.last_live'),
      isNoteAReason: isHeldBack,
      removeLabel: review.removeLaunches ? t(draftCampaign ? 'editor.remove_launch' : 'editor.remove_schedule') : t('editor.remove'),
      canUndo: post.reviewed,
      primary: { ...primary, isHeldBack },
      isRemoveIconOnlyOnPhone: post.reviewed,
      isLaunch: review.last,
    }
  }

  if (post) {
    const label = dirty ? t('editor.save') : t('editor.done')
    return {
      note: isHeldBack ? blockerText : null,
      isNoteAReason: isHeldBack,
      removeLabel: removable ? t('editor.remove') : undefined,
      canUndo: false,
      primary: { label, phoneLabel: label, isHeldBack, onClick: on.close },
      isRemoveIconOnlyOnPhone: false,
      isLaunch: false,
    }
  }

  const label = t('editor.schedule_post')
  return {
    note: blockerText,
    isNoteAReason: !!blockerText,
    canUndo: false,
    primary: { label, phoneLabel: label, icon: <CheckOutlined />, isHeldBack: !!blockerText, onClick: on.create },
    isRemoveIconOnlyOnPhone: false,
    isLaunch: false,
  }
}

interface PagerProps {
  /** Where the post stands: "Post 2 / 7" */
  index: number
  total: number
  hasPrevious: boolean
  hasNext: boolean
  /** An action is on its way: the walk waits */
  busy: boolean
  onPrevious: () => void
  onNext: () => void
}

/**
 * The editor's pager, left of its actions: Previous, "Post 2 / 7", Next. A button that cannot walk keeps the focus
 * (`aria-disabled`), so walking to an end with the keyboard does not drop it.
 */
export const PostEditorPager: FC<PagerProps> = ({ index, total, hasPrevious, hasNext, busy, onPrevious, onNext }) => {
  const { t } = useTranslation(['social_campaign'])

  return (
    <div className={styles.pager}>

      <MUIButton
        type="secondaryBorder"
        size="sm"
        iconOnly
        className={styles.navButton}
        aria-label={t('editor.previous')}
        disabled={!hasPrevious || busy}
        isFocusableWhenDisabled
        onClick={onPrevious}
      >
        <ChevronLeftOutlined />
      </MUIButton>

      <Typography variant="text-sm" component="span" color={GRAY_700} className={styles.pos}>{t('editor.pos', { index: index + 1, total })}</Typography>

      <MUIButton
        type="secondaryBorder"
        size="sm"
        iconOnly
        className={styles.navButton}
        aria-label={t('editor.next_post')}
        disabled={!hasNext || busy}
        isFocusableWhenDisabled
        onClick={onNext}
      >
        <ChevronRightOutlined />
      </MUIButton>

    </div>
  )
}

interface ActionsProps {
  model: PostEditorActionsModel
  /** An action is on its way: every action waits, keeping the focus it had */
  busy: boolean
  /** Id of the note, when it says why the main action waits */
  reasonId: string
  onRemove: () => void
  onUndo: () => void
}

/** The editor's actions in its foot (from 600px; a post written by hand shows them on a phone too). */
export const PostEditorFootActions = forwardRef<HTMLButtonElement, ActionsProps>(function PostEditorFootActions({ model, busy, reasonId, onRemove, onUndo }, primaryRef) {
  const { t } = useTranslation(['social_campaign'])
  const { primary } = model

  return (
    <div className={styles.acts} data-foot-actions="">

      {model.note &&
        <Typography id={model.isNoteAReason ? reasonId : undefined} variant="text-sm" component="span" color={GRAY_700} className={styles.count}>{model.note}</Typography>
      }

      {model.removeLabel &&
        <MUIButton type="orangeSecondaryNoBorder" className={styles.actButton} startIcon={<DeleteOutlineOutlined />} disabled={busy} isFocusableWhenDisabled onClick={onRemove}>{model.removeLabel}</MUIButton>
      }

      {model.canUndo &&
        <MUIButton type="secondaryBorder" className={styles.actButton} startIcon={<UndoOutlined />} disabled={busy} isFocusableWhenDisabled onClick={onUndo}>{t('editor.undo')}</MUIButton>
      }

      <MUIButton
        ref={primaryRef}
        className={styles.actButton}
        startIcon={primary.icon}
        disabled={busy || primary.isHeldBack}
        isFocusableWhenDisabled
        aria-describedby={primary.isHeldBack && model.isNoteAReason ? reasonId : undefined}
        onClick={primary.onClick}
      >
        {primary.label}
      </MUIButton>

    </div>
  )
})

/**
 * The post's actions on a phone, floating above the foot in the dark bar. The main action stays pressable while a
 * change cannot be saved: the editor says why once it is tapped (the foot's reason is hidden there).
 */
export const PostEditorFab: FC<Omit<ActionsProps, 'reasonId'>> = ({ model, busy, onRemove, onUndo }) => {
  const { t } = useTranslation(['social_campaign'])
  const isNarrow = useMatchMedia(NARROW_PHONE_QUERY)
  const { primary } = model
  // the last draft's two long labels ("Remove & launch", "Approve & launch") only fit without their glyphs
  const glyph = (icon: ReactElement | undefined) => model.isLaunch && isNarrow ? undefined : icon

  return (
    <ActionBar position="static" role="toolbar" aria-label={t('editor.fab_label')} className={styles.fab}>

      {model.removeLabel &&
        <ActionBarButton
          label={model.removeLabel}
          icon={model.isRemoveIconOnlyOnPhone ? <DeleteOutlineOutlined /> : glyph(<DeleteOutlineOutlined />)}
          isIconOnly={model.isRemoveIconOnlyOnPhone}
          isDestructive
          disabled={busy}
          isFocusableWhenDisabled
          onClick={onRemove}
        />
      }

      {model.canUndo && <ActionBarButton label={t('editor.undo')} icon={glyph(<UndoOutlined />)} disabled={busy} isFocusableWhenDisabled onClick={onUndo} />}

      <ActionBarButton label={primary.phoneLabel} icon={glyph(primary.icon)} isPrimary disabled={busy} isFocusableWhenDisabled onClick={primary.onClick} />

    </ActionBar>
  )
}
